import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, X, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    quote: '',
    name: '',
    car: '',
    rally: '',
    order: 0
  });

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditing(null);
    setFormData({ quote: '', name: '', car: '', rally: '', order: testimonials.length + 1 });
    setShowModal(true);
  };

  const openEditModal = (testimonial) => {
    setEditing(testimonial);
    setFormData({
      quote: testimonial.quote,
      name: testimonial.name,
      car: testimonial.car || '',
      rally: testimonial.rally || '',
      order: testimonial.order || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const payload = {
      ...formData,
      order: parseInt(formData.order) || 0
    };

    try {
      if (editing) {
        await axios.put(`${API_URL}/api/testimonials/${editing.id}`, payload, { headers });
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API_URL}/api/testimonials`, payload, { headers });
        toast.success('Testimonial created');
      }
      setShowModal(false);
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6]">TESTIMONIALS</h1>
        <button
          onClick={openCreateModal}
          data-testid="add-testimonial-btn"
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          Add Testimonial
        </button>
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : testimonials.length === 0 ? (
        <p className="text-[#A19D98]">No testimonials yet.</p>
      ) : (
        <div className="grid gap-4">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              data-testid={`testimonial-row-${testimonial.id}`}
              className="bg-[#141414] border border-[#2A2A2A] p-6"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <Quote className="w-6 h-6 text-[#E8651A]/50 mb-2" />
                  <p className="text-[#F2EDE6] italic mb-4">"{testimonial.quote}"</p>
                  <p className="text-[#E8651A] font-['Oswald']">{testimonial.name}</p>
                  <p className="text-[#A19D98] text-sm">{testimonial.car}</p>
                  <p className="text-[#A19D98] text-xs">{testimonial.rally}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => openEditModal(testimonial)}
                    className="p-2 text-[#A19D98] hover:text-[#E8651A]"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    className="p-2 text-[#A19D98] hover:text-[#FF3B30]"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <div className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6]">
                {editing ? 'EDIT TESTIMONIAL' : 'ADD TESTIMONIAL'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-[#A19D98] hover:text-[#F2EDE6]">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Quote *</label>
                <Textarea
                  name="quote"
                  value={formData.quote}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  data-testid="testimonial-quote-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="John D."
                    data-testid="testimonial-name-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Car</label>
                  <Input
                    name="car"
                    value={formData.car}
                    onChange={handleInputChange}
                    placeholder="2020 Toyota Supra"
                    data-testid="testimonial-car-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Rally</label>
                  <Input
                    name="rally"
                    value={formData.rally}
                    onChange={handleInputChange}
                    placeholder="Austin Tour 2026"
                    data-testid="testimonial-rally-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Order</label>
                  <Input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    data-testid="testimonial-order-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" data-testid="testimonial-submit-btn" className="btn-primary flex-1">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
